/* eslint-disable no-param-reassign */
import { createSelector } from 'reselect';
import transform from 'lodash.transform';
import groupBy from 'lodash.groupby';
import get from 'lodash.get';
import sumBy from 'lodash.sumby';

// Selectors
import { makeSelectTimesheetValue } from 'services/timesheet/selectors';
import { selectTimesheetTimesheetEntries } from 'services/timesheetTimesheetEntries/selectors';
import { selectWorkTypes } from 'services/workTypes/selectors';
import { selectEntities } from 'services/entities/selectors';
import { makeSelectFinalForm } from 'services/finalForms/selectors';
import { selectFormattedPeople as selectPeople } from 'services/people/selectors';
import { selectTimesheetGroupBy } from 'services/ui/selectors';
import {
    selectIsAdmin,
    selectIsManager,
    selectIsAssistantManager,
    makeSelectUsersMeValue
} from 'services/usersMe/selectors';

// Utils
import { formatEntryForForm } from 'utils/timesheets';
import createDeepEqualSelector from 'utils/reselect/createDeepEqualSelector';
import orderBy from 'utils/custom-orderBy';

// Workspaces
import { makeSelectQueryStringValue } from 'workspaces/react-router-selectors';

const selectState = state => state;
const selectRowId = (state, props) => props.rowId || props.timesheetEntryUuid;
const selectCellName = (state, props) => props.cellName;
const selectTableColumnConfig = () => [
    {
        label: 'Date',
        value: 'date',
        name: 'date',
        selectable: true
    },
    {
        label: 'Full Name',
        value: 'workType.fullName',
        name: 'fullName',
        selectable: true
    },
    {
        label: 'Task',
        value: 'workType.name',
        name: 'workTypeCompositeKey',
        selectable: true
    },
    {
        label: 'Area',
        value: 'entity.name',
        name: 'entityCompositeKey',
        selectable: true
    },
    {
        label: 'Start Time',
        value: 'startTime',
        name: 'startTime',
        selectable: true
    },
    {
        label: 'End Time',
        value: 'endTime',
        name: 'endTime',
        selectable: true
    },
    {
        label: 'Lun. Brk',
        value: 'unpaidBreakMinutes',
        name: 'unpaidBreakMinutes',
        selectable: true
    },
    {
        label: 'Hours',
        value: 'totalPaidHours',
        selectable: false
    },
    {
        label: 'Hours',
        value: 'totalPieceRateHours',
        selectable: false
    },
    {
        label: 'Count',
        value: 'units',
        name: 'units',
        selectable: true
    },
    {
        label: 'Rate',
        value: 'rate',
        name: 'rate',
        isPieceRateField: true,
        selectable: true
    },
    {
        label: 'Pd.Brk',
        value: 'paidBreakMinutes',
        name: 'paidBreakMinutes',
        isPieceRateField: true,
        selectable: true
    },
    {
        label: 'Earnings',
        value: 'Earnings',
        selectable: false
    },
    {
        label: 'Achieved',
        value: 'effectiveRate',
        selectable: false
    },
    {
        label: 'Pd.Brk',
        value: 'paidBreakDollars',
        selectable: false
    }
];

const selectGroupingId = (state, props) => props.groupingId;
const selectIsPieceRate = (state, props) => props.isPieceRate;
const selectFieldName = (state, props) => props.fieldName;

const selectInitialValues = createSelector(
    [
        selectTimesheetTimesheetEntries,
        selectWorkTypes,
        selectEntities,
        selectPeople
    ],
    (
        timesheetTimesheetEntriesState,
        workTypesState,
        entitiesState,
        peopleState
    ) => {
        return transform(
            timesheetTimesheetEntriesState,
            (result, value) => {
                const {
                    uuid,
                    workTypeId,
                    entityId,
                    personId,
                    lastReviewIsApproved
                } = value;
                const isPieceRate = get(workTypesState, [
                    workTypeId,
                    'isPieceRate'
                ]);
                const workTypeEntityTypeId = get(workTypesState, [
                    workTypeId,
                    'entityTypeId'
                ]);
                const entityEntityTypeId = get(entitiesState, [
                    entityId,
                    'entityTypeId'
                ]);

                const person = get(peopleState, personId);

                // HACK hide submitted entries if the person they belong to has been deleted
                if (lastReviewIsApproved || person) {
                    result[uuid] = formatEntryForForm({
                        ...value,
                        workTypeCompositeKey: `${workTypeId}.${isPieceRate}.${workTypeEntityTypeId}`,
                        entityCompositeKey: `${entityId}.${entityEntityTypeId}`
                    });
                }
            },
            null
        );
    }
);

const selectSelectableTableColumns = createDeepEqualSelector(
    selectTableColumnConfig,
    tableColumnConfigState =>
        tableColumnConfigState.filter(value => value.selectable)
);

const selectSelectableTableColumnNames = createDeepEqualSelector(
    selectTableColumnConfig,
    tableColumnConfigState =>
        tableColumnConfigState
            .filter(value => value.selectable)
            .map(value => value.name)
);

const selectForm = createDeepEqualSelector(
    [selectState, makeSelectTimesheetValue('id')],
    (state, timesheetIdState) => {
        return (
            makeSelectFinalForm(
                `TimesheetTimesheetEntriesForm.${timesheetIdState}`
            )(state) || {}
        );
    }
);

const selectActiveField = createDeepEqualSelector(
    selectForm,
    formState => formState.active
);

const selectValues = createDeepEqualSelector(
    selectForm,
    formState => formState.values
);

const selectValuesArray = createDeepEqualSelector(
    selectValues,
    (valuesState = {}) => Object.values(valuesState)
);

const selectValuesTimesheetEntry = createDeepEqualSelector(
    [selectValues, selectRowId],
    (valuesState = [], rowIdProp) => rowIdProp && valuesState[rowIdProp]
);

const makeSelectValuesTimesheetEntry = () => selectValuesTimesheetEntry;

const makeSelectValuesTimesheetEntryValue = fieldName =>
    createDeepEqualSelector(
        [selectValuesTimesheetEntry, selectCellName],
        (valuesTimesheetEntryState, cellNameProp) => {
            const field = fieldName || cellNameProp;
            // TODO use get
            return (
                valuesTimesheetEntryState && valuesTimesheetEntryState[field]
            );
        }
    );

const selectErrors = createDeepEqualSelector(
    selectForm,
    formState => formState.errors
);

const selectErrorsTimesheetEntries = createDeepEqualSelector(
    selectErrors,
    errorsState => errorsState
);

const selectErrorsTimesheetEntryUuids = createDeepEqualSelector(
    selectErrorsTimesheetEntries,
    errorsTimesheetEntriesState =>
        errorsTimesheetEntriesState && Object.keys(errorsTimesheetEntriesState)
);

const selectErrorsTimesheetEntry = createDeepEqualSelector(
    [selectErrorsTimesheetEntries, selectRowId],
    (errorsTimesheetEntriesState, rowIdProp) =>
        errorsTimesheetEntriesState && errorsTimesheetEntriesState[rowIdProp]
);

const makeSelectFormErrorExists = () =>
    createDeepEqualSelector(
        [selectErrorsTimesheetEntries, selectRowId],
        (errorsTimesheetEntriesState, rowIdProp) =>
            !!errorsTimesheetEntriesState[rowIdProp]
    );

const makeSelectErrorsTimesheetEntryValue = () =>
    createDeepEqualSelector(
        [selectErrorsTimesheetEntry, selectCellName],
        (errorsTimesheetEntryState, cellNameProp) =>
            errorsTimesheetEntryState && errorsTimesheetEntryState[cellNameProp]
    );

const selectDenormalizedData = createDeepEqualSelector(
    [selectValuesArray, selectEntities, selectPeople, selectWorkTypes],
    (valuesArrayState, entitiesState, peopleState, workTypesState) =>
        valuesArrayState.map(value => {
            const {
                workTypeCompositeKey,
                entityCompositeKey,
                personId,
                personFirstName,
                personLastName
            } = value;

            const [workTypeIdString] = workTypeCompositeKey
                ? workTypeCompositeKey.split('.')
                : [];

            const workType = get(workTypesState, workTypeIdString, {});

            const [entityIdString] = entityCompositeKey
                ? entityCompositeKey.split('.')
                : [];

            const person = get(peopleState, personId, {
                firstName: personFirstName,
                lastName: personLastName,
                fullName: `${
                    personFirstName ? `${personFirstName} ` : ''
                }${personLastName}`
            });

            const entity = get(entitiesState, entityIdString, {});

            return {
                ...value,
                workType,
                person,
                entity
            };
        })
);

const selectFilteredData = createDeepEqualSelector(
    [
        selectDenormalizedData,
        makeSelectQueryStringValue('people'),
        makeSelectQueryStringValue('workTypes')
    ],
    (denormalizedDataState, peopleState, workTypesState) =>
        denormalizedDataState.filter(value => {
            if (!value.uuid) return false;
            const { personId, workTypeCompositeKey } = value;

            const [workTypeIdString] = workTypeCompositeKey
                ? workTypeCompositeKey.split('.')
                : [];

            if (peopleState && !peopleState.includes(`${personId}`)) {
                return false;
            }
            if (workTypesState && !workTypesState.includes(workTypeIdString)) {
                return false;
            }
            return value;
        })
);

const selectPieceRateEntryExists = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState =>
        filteredDataState.some(value => {
            const [, isPieceRateString] = value.workTypeCompositeKey
                ? value.workTypeCompositeKey.split('.')
                : [];
            const isPieceRate = isPieceRateString === 'true';
            return isPieceRate;
        })
);

const selectHourlyRateEntryExists = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState =>
        filteredDataState.some(value => {
            const [, isPieceRateString] = value.workTypeCompositeKey
                ? value.workTypeCompositeKey.split('.')
                : [];
            const isPieceRate = isPieceRateString === 'true';
            return !isPieceRate;
        })
);

const selectGroupBy = createDeepEqualSelector(
    selectTimesheetGroupBy,
    timesheetGroupByState => timesheetGroupByState || 'timesheetId'
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredData, selectGroupBy, makeSelectQueryStringValue('sort')],
    (filteredDataState, groupByState, sortState = 'date_ascending') => {
        const attributes = sortState.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';
        return orderBy(
            filteredDataState,
            [groupByState, propertyName, 'startTime', 'uuid'],
            ['asc', order, 'asc', 'asc']
        );
    }
);

const selectOrderedDataIds = createDeepEqualSelector(
    selectOrderedData,
    orderedDataState => orderedDataState.map(value => value.uuid)
);

const selectGroupedData = createDeepEqualSelector(
    [selectOrderedData, selectGroupBy],
    (orderedDataState, groupByState) => {
        return groupBy(orderedDataState, groupByState);
    }
);

const selectGroupedDataKeys = createDeepEqualSelector(
    selectGroupedData,
    groupedDataState => Object.keys(groupedDataState)
);

const selectIds = createDeepEqualSelector(
    selectGroupedData,
    groupedDataState => groupedDataState
);

const selectGroupedDataValue = createDeepEqualSelector(
    [selectGroupedData, selectGroupingId],
    (groupedDataState, groupingIdProp) => groupedDataState[groupingIdProp]
);

const selectFilteredGroupedDataValue = createDeepEqualSelector(
    [selectGroupedDataValue, selectIsPieceRate],
    (groupedDataValueState, isPieceRateProp) => {
        if (typeof isPieceRateProp !== 'boolean') return groupedDataValueState;
        return (
            groupedDataValueState &&
            groupedDataValueState.filter(value => {
                const [, isPieceRateString] = value.workTypeCompositeKey
                    ? value.workTypeCompositeKey.split('.')
                    : [];
                const isPieceRate = isPieceRateString === 'true';
                return isPieceRate === isPieceRateProp;
            })
        );
    }
);

const makeSelectGroupedDataValueIds = () =>
    createDeepEqualSelector(
        selectGroupedDataValue,
        groupedDataValueState =>
            groupedDataValueState &&
            groupedDataValueState.map(value => value.uuid)
    );

const makeSelectGroupedDataValueAverage = () =>
    createDeepEqualSelector(
        [selectFilteredGroupedDataValue, selectFieldName],
        (filteredGroupedDataValueState, fieldNameState) => {
            if (
                !filteredGroupedDataValueState ||
                !filteredGroupedDataValueState.length
            ) {
                return 0;
            }
            const sum = filteredGroupedDataValueState.reduce(
                (accumulator, currentValue) =>
                    accumulator + (Number(currentValue[fieldNameState]) || 0),
                0
            );
            return sum / filteredGroupedDataValueState.length;
        }
    );

const makeSelectGroupedDataValueAverageEffectiveRate = () =>
    createDeepEqualSelector(
        selectFilteredGroupedDataValue,
        filteredGroupedDataValueState => {
            if (
                !filteredGroupedDataValueState ||
                !filteredGroupedDataValueState.length
            ) {
                return 0;
            }

            const sumTotalPieceRateHours = sumBy(
                filteredGroupedDataValueState,
                'totalPieceRateHours'
            );
            const sumEarnings = sumBy(
                filteredGroupedDataValueState,
                'earnings'
            );
            return sumEarnings / sumTotalPieceRateHours;
        }
    );

const makeSelectGroupedDataValueTotal = () =>
    createDeepEqualSelector(
        [selectGroupedDataValue, selectFieldName],
        (groupedDataValueState, fieldNameState) => {
            if (!groupedDataValueState || !groupedDataValueState.length) {
                return 0;
            }
            return groupedDataValueState.reduce(
                (accumulator, currentValue) =>
                    accumulator + (Number(currentValue[fieldNameState]) || 0),
                0
            );
        }
    );

const makeSelectUserHasPermissionToEdit = () =>
    createDeepEqualSelector(
        [
            selectIsAdmin,
            selectIsManager,
            selectIsAssistantManager,
            makeSelectUsersMeValue('isSuperAdmin')
        ],
        (
            isAdminState,
            isManagerState,
            isAssistantManagerState,
            isSuperAdminState
        ) =>
            isAdminState ||
            isManagerState ||
            isAssistantManagerState ||
            isSuperAdminState
    );

export {
    selectInitialValues,
    selectSelectableTableColumns,
    selectSelectableTableColumnNames,
    selectActiveField,
    selectGroupBy,
    selectPieceRateEntryExists,
    selectHourlyRateEntryExists,
    selectOrderedData,
    selectOrderedDataIds,
    selectGroupedData,
    selectGroupedDataKeys,
    selectIds,
    selectValuesArray,
    makeSelectValuesTimesheetEntry,
    makeSelectValuesTimesheetEntryValue,
    selectErrorsTimesheetEntryUuids,
    selectErrorsTimesheetEntry,
    makeSelectFormErrorExists,
    makeSelectErrorsTimesheetEntryValue,
    selectGroupedDataValue,
    selectFilteredGroupedDataValue,
    makeSelectGroupedDataValueIds,
    makeSelectGroupedDataValueAverage,
    makeSelectGroupedDataValueAverageEffectiveRate,
    makeSelectGroupedDataValueTotal,
    makeSelectUserHasPermissionToEdit
};
